import {
  Component, ViewEncapsulation, ChangeDetectionStrategy, Input, Output, EventEmitter
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import { HotelFilterInputTypes, HotelFilterTypes } from '@shared/hotel-itinerary/hotel-filters';
import { CheckboxFilter, CheckboxFilterVm, PriceRangeFilter } from '@shared/filters/entities/fetch-filters.response';
import {
  HOTEL_FILTER_BAR_TAGS, HOTEL_FILTER_BAR_TAG_ICONS, PRIME_KEY, RAPPICARD_KEY
} from '@shared/magic-filter-bar/hotel-filter-bar/mappers/hotel-filters-in-bar.mapper';
import { HOTEL_NON_REFUNDABLE_TITLES, NO_ZONE_LABEL, STARS_LABEL, STAR_LABEL } from '@hotels/hotel-results/constants/filters';
import { HotelFilterBarService } from '@shared/magic-filter-bar/hotel-filter-bar/services/hotel-filter-bar.service';
import { isEmpty } from '@app/util/util';
import { HotelFilterVm } from '@shared/magic-filter-bar/entities/filter-types';
import { hotelFilterIsNoZoneLabel, hotelFilterNeedsTranslation } from '@app/mappers/global-filters/hotel-global-filters.mapper';
import { getLegendByScore } from '@shared/score-detail/helpers/label-score.helper';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';

@Component({
  selector: 'app-hotel-filter-bar-slide',
  templateUrl: './hotel-filter-bar-slide.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HotelFilterBarSlideComponent {

  @Input() barFilters: HotelFilterVm[];
  @Input() showExclusiveOfferFilter: boolean;
  @Input() tagTypeIsRappicard: boolean;
  @Input() tagTypeIsPrime: boolean;

  @Input() pickedTotalDiscountPercentage = 0;
  @Input() pickedMemberRatesOnly = false;
  @Input() selectedStarItems = [];
  @Input() pickedCheckboxes = [];
  @Input() pickedStars = [];

  @Output() exclusiveOfferFilterEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() checkboxEvent: EventEmitter<{
    checked: boolean,
    isMultiple: boolean,
    isCheckbox: boolean,
    filter: HotelFilterVm
  }> = new EventEmitter<{
    checked: boolean,
    isMultiple: boolean,
    isCheckbox: boolean,
    filter: HotelFilterVm
  }>();
  @Output() radioEvent: EventEmitter<{
    filter: HotelFilterVm,
    isMultiple: boolean,
  }> = new EventEmitter<{
    filter: HotelFilterVm,
    isMultiple: boolean,
  }>();

  constructor(
    private readonly translateService: TranslocoService,
    private readonly hotelFilterBarService: HotelFilterBarService,
  ) { }

  get hotelFilterTypes(): typeof HotelFilterTypes {
    return HotelFilterTypes;
  }

  public isCheckboxFilterTypes(filter: HotelFilterVm): boolean {
    return this.isCheckboxFilter(filter) || this.isStarsFilter(filter);
  }

  public isRadioFilter(filter: HotelFilterVm): boolean {
    const type = filter.type;
    const isRadio = filter.filter === HotelFilterInputTypes.Radio;
    if (type === HotelFilterTypes.HotelPriceRanges) {
      return isRadio && (filter.value as PriceRangeFilter[]).length > 0;
    } else if (type === HotelFilterTypes.PromotionalRates) {
      return isRadio && (filter.value as PriceRangeFilter[]).length > 0;
    } else {
      return false;
    }
  }

  public exclusiveIconTag(filter: HotelFilterVm): Icon {
    const key = this.tagTypeIsPrime ? PRIME_KEY : this.tagTypeIsRappicard ? RAPPICARD_KEY : '';
    return this.getIconTag(filter)?.[key];
  }

  public exclusiveLabel(filter: HotelFilterVm): string {
    const key = this.tagTypeIsPrime ? PRIME_KEY : this.tagTypeIsRappicard ? RAPPICARD_KEY : '';
    return !isEmpty(key) ? this.translateService.translate(HOTEL_FILTER_BAR_TAGS[filter.type][key]) : '';
  }

  public getIconTag(filter: HotelFilterVm): Icon {
    return HOTEL_FILTER_BAR_TAG_ICONS[filter.type];
  }

  public checkedCheckbox(filter: HotelFilterVm): boolean {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const includes = [...filter.value as any[]].map(item => {
      return this.checkedByCheckboxItem(item, filter);
    });
    return includes.reduce((accumulator, currentValue) => accumulator || currentValue, false);
  }

  public getCheckboxLabel(filter: HotelFilterVm): string | number {
    if (this.checkedCheckbox(filter)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return [...filter.value as any[]].filter(item => this.checkedByCheckboxItem(item, filter)).map(item => {
        return hotelFilterNeedsTranslation(filter.type) ? this.labelByFilterWithTranslation(item, filter.type) :
          item.TextTransformed ?? hotelFilterIsNoZoneLabel(item) ? this.translateService.translate(NO_ZONE_LABEL) : 
            filter.type === HotelFilterTypes.Score ? this.translateService.translate(getLegendByScore(item.Text)) : item.Text;
      }).join(', ');
    } else {
      return this.getDefaultLabel(filter);
    }
  }

  public getDiscountPercentageLabel(filter: HotelFilterVm): string {
    if (this.pickedTotalDiscountPercentage) {
      const valueOfFilter = [
        ...filter.value as PriceRangeFilter[]
      ].find(item => item.LowerBound === this.pickedTotalDiscountPercentage);
      return valueOfFilter.Text;
    } else {
      return this.getDefaultLabel(filter);
    }
  }

  public isMultipleSelection(type: string): boolean {
    return type === HotelFilterTypes.Category || 
           type === HotelFilterTypes.PromotionalRates || 
           type === HotelFilterTypes.CheckinTimes ||
           type === HotelFilterTypes.Score;
  }

  public isExclusiveOfferFilter(item: HotelFilterVm): boolean {
    return item.type === HotelFilterTypes.MemberRatesOnly && (item.value as CheckboxFilter[]).length > 0;
  }

  public isCheckboxFilter(filter: HotelFilterVm): boolean {
    return filter.filter === HotelFilterInputTypes.Checkbox && (filter.value as CheckboxFilter[]).length > 0;
  }

  public checkboxTrigger(filter: HotelFilterVm): void {
    this.checkboxEvent.emit({
      filter: filter,
      checked: this.checkedCheckbox(filter),
      isMultiple: this.isMultipleSelection(filter.type),
      isCheckbox: this.isCheckboxFilter(filter)
    });
  }

  public radioTrigger(filter: HotelFilterVm): void {
    this.radioEvent.emit({
      filter: filter,
      isMultiple: this.isMultipleSelection(filter.type),
    });
  }

  private isStarsFilter(filter: HotelFilterVm): boolean {
    return filter.filter === HotelFilterInputTypes.Stars && (filter.value as CheckboxFilter[]).length > 0;
  }

  private labelByFilterWithTranslation(item: CheckboxFilter, type: string): string {
    if (type === HotelFilterTypes.NonRefundable) {
      return this.translateService.translate(HOTEL_NON_REFUNDABLE_TITLES[item.Text]);
    } else if (type === HotelFilterTypes.Category) {
      return `${item.Text} ${this.translateService.translate(parseInt(item.Text) === 1 ? STAR_LABEL : STARS_LABEL)}`;
    } else {
      return item.Text;
    }
  }

  private checkedByCheckboxItem(item: CheckboxFilterVm, filter: HotelFilterVm): boolean {
    const validate = this.hotelFilterBarService.checkedHotelCheckbox(
      this.pickedCheckboxes, this.pickedStars, this.selectedStarItems, item, this.isCheckboxFilter(filter)
    );
    if (validate.addStars) {
      this.selectedStarItems.push(item);
    }
    return validate.include;
  }

  private getDefaultLabel(filter: HotelFilterVm): string {
    return this.translateService.translate(HOTEL_FILTER_BAR_TAGS[filter.type]);
  }

}
